import { PageHeader } from '../components/PageHeader';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  MAP_PROVIDERS,
  PAGINATION,
  updateSettingsSchema,
  type Settings,
  type UpdateSettingsInput,
} from '@fdt/shared';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, errorKey } from '../api/client';
import { useFormError } from '../hooks/useFormError';
import { warehousesApi } from '../resources';
import { zodFieldErrors } from '../lib/zod-field-errors';

interface FormState {
  mapProvider: Settings['mapProvider'];
  osmTileUrl: string;
  /** Empty means "leave the stored key alone". */
  googleApiKey: string;
  deviationDistanceMeters: string;
  deviationMinDurationSeconds: string;
  locationIntervalSeconds: string;
  locationDistanceMeters: string;
  locationAccuracyLimitMeters: string;
  locationRetentionDays: string;
  defaultGeofenceRadiusMeters: string;
  allowStaffPriceOverride: boolean;
  vatRates: string;
  defaultWarehouseId: string;
  consentTextIt: string;
  consentTextTr: string;
}

function toForm(settings: Settings): FormState {
  return {
    mapProvider: settings.mapProvider,
    osmTileUrl: settings.osmTileUrl,
    googleApiKey: '',
    deviationDistanceMeters: String(settings.deviationDistanceMeters),
    deviationMinDurationSeconds: String(settings.deviationMinDurationSeconds),
    locationIntervalSeconds: String(settings.locationIntervalSeconds),
    locationDistanceMeters: String(settings.locationDistanceMeters),
    locationAccuracyLimitMeters: String(settings.locationAccuracyLimitMeters),
    locationRetentionDays: String(settings.locationRetentionDays),
    defaultGeofenceRadiusMeters: String(settings.defaultGeofenceRadiusMeters),
    allowStaffPriceOverride: settings.allowStaffPriceOverride,
    vatRates: settings.vatRates.join(', '),
    defaultWarehouseId: settings.defaultWarehouseId ?? '',
    consentTextIt: settings.trackingConsentText.it ?? '',
    consentTextTr: settings.trackingConsentText.tr ?? '',
  };
}

export function SettingsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: async () => (await api.get<Settings>('/settings')).data,
  });

  const warehouses = useQuery({
    queryKey: ['warehouses', 'all'],
    queryFn: () => warehousesApi.list({ page: 1, pageSize: PAGINATION.maxPageSize }),
  });

  const [form, setForm] = useState<FormState | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const formError = useFormError();

  // Fill the form once the settings arrive, and after each successful save.
  useEffect(() => {
    if (settings.data) {
      setForm(toForm(settings.data));
    }
  }, [settings.data]);

  const save = useMutation({
    mutationFn: async (body: UpdateSettingsInput) =>
      (await api.patch<Settings>('/settings', body)).data,
    onSuccess: (updated) => {
      queryClient.setQueryData(['settings'], updated);
      setSaved(true);
    },
  });

  function submit(): void {
    if (!form) {
      return;
    }
    formError.clear();

    const vatRates = form.vatRates
      .split(',')
      .map((rate) => rate.trim())
      .filter(Boolean)
      .map(Number);

    if (vatRates.some(Number.isNaN)) {
      setFieldErrors({ vatRates: 'validation.decimal' });
      return;
    }

    const payload = {
      mapProvider: form.mapProvider,
      osmTileUrl: form.osmTileUrl,
      // Only sent when something was typed; the stored key is never echoed back.
      ...(form.googleApiKey.trim() ? { googleApiKey: form.googleApiKey.trim() } : {}),
      deviationDistanceMeters: form.deviationDistanceMeters,
      deviationMinDurationSeconds: form.deviationMinDurationSeconds,
      locationIntervalSeconds: form.locationIntervalSeconds,
      locationDistanceMeters: form.locationDistanceMeters,
      locationAccuracyLimitMeters: form.locationAccuracyLimitMeters,
      locationRetentionDays: form.locationRetentionDays,
      defaultGeofenceRadiusMeters: form.defaultGeofenceRadiusMeters,
      allowStaffPriceOverride: form.allowStaffPriceOverride,
      vatRates,
      defaultWarehouseId: form.defaultWarehouseId || null,
      trackingConsentText: { it: form.consentTextIt, tr: form.consentTextTr },
    };

    const parsed = updateSettingsSchema.safeParse(payload);
    if (!parsed.success) {
      setFieldErrors(zodFieldErrors(parsed));
      return;
    }

    setFieldErrors({});
    save.mutate(parsed.data, { onError: (error) => formError.capture(error) });
  }

  if (settings.isPending || !form) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (settings.isError) {
    return <Alert severity="error">{t(errorKey(settings.error))}</Alert>;
  }

  const numberField = (
    key: keyof FormState,
    label: string,
    helper?: string,
  ) => (
    <TextField
      label={label}
      value={String(form[key])}
      onChange={(event) => setForm({ ...form, [key]: event.target.value })}
      error={Boolean(fieldErrors[key])}
      helperText={fieldErrors[key] ? t(fieldErrors[key]!) : helper}
      inputMode="numeric"
      fullWidth
    />
  );

  return (
    <Stack spacing={3} sx={{ maxWidth: 900 }}>
      <PageHeader icon={SettingsIcon} tone="neutral" title={t('nav.settings')} />

      {formError.errorKey && <Alert severity="error">{t(formError.errorKey)}</Alert>}

      <Card>
        <CardContent>
          <Typography variant="h2" gutterBottom>
            {t('settings.mapSection')}
          </Typography>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label={t('settings.mapProvider')}
                value={form.mapProvider}
                onChange={(event) =>
                  setForm({ ...form, mapProvider: event.target.value as Settings['mapProvider'] })
                }
                sx={{ minWidth: 220 }}
              >
                {MAP_PROVIDERS.map((provider) => (
                  <MenuItem key={provider} value={provider}>
                    {t(`settings.providers.${provider}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('settings.defaultWarehouse')}
                value={form.defaultWarehouseId}
                onChange={(event) => setForm({ ...form, defaultWarehouseId: event.target.value })}
                helperText={t('settings.defaultWarehouseHelp')}
                fullWidth
              >
                <MenuItem value="">—</MenuItem>
                {(warehouses.data?.items ?? []).map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <TextField
              label={t('settings.tileUrl')}
              value={form.osmTileUrl}
              onChange={(event) => setForm({ ...form, osmTileUrl: event.target.value })}
              error={Boolean(fieldErrors.osmTileUrl)}
              helperText={
                fieldErrors.osmTileUrl ? t(fieldErrors.osmTileUrl) : t('settings.tileUrlHelp')
              }
              fullWidth
            />

            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label={t('settings.googleApiKey')}
                value={form.googleApiKey}
                onChange={(event) => setForm({ ...form, googleApiKey: event.target.value })}
                type="password"
                autoComplete="off"
                error={Boolean(fieldErrors.googleApiKey)}
                helperText={
                  fieldErrors.googleApiKey
                    ? t(fieldErrors.googleApiKey)
                    : t('settings.googleApiKeyHelp')
                }
                fullWidth
              />
              <Chip
                size="small"
                color={settings.data?.hasGoogleApiKey ? 'success' : 'default'}
                variant={settings.data?.hasGoogleApiKey ? 'filled' : 'outlined'}
                label={
                  settings.data?.hasGoogleApiKey
                    ? t('settings.keyStored')
                    : t('settings.keyMissing')
                }
              />
              {settings.data?.hasGoogleApiKey && (
                <Button
                  color="error"
                  onClick={() =>
                    save.mutate(
                      { googleApiKey: null },
                      { onError: (error) => formError.capture(error) },
                    )
                  }
                >
                  {t('settings.removeKey')}
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h2" gutterBottom>
            {t('settings.trackingSection')}
          </Typography>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {numberField(
                'locationIntervalSeconds',
                t('settings.locationInterval'),
                t('settings.locationIntervalHelp'),
              )}
              {numberField(
                'locationDistanceMeters',
                t('settings.locationDistance'),
                t('settings.locationDistanceHelp'),
              )}
              {numberField(
                'locationAccuracyLimitMeters',
                t('settings.accuracyLimit'),
                t('settings.accuracyLimitHelp'),
              )}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {numberField(
                'deviationDistanceMeters',
                t('settings.deviationDistance'),
                t('settings.deviationDistanceHelp'),
              )}
              {numberField(
                'deviationMinDurationSeconds',
                t('settings.deviationDuration'),
                t('settings.deviationDurationHelp'),
              )}
              {numberField(
                'defaultGeofenceRadiusMeters',
                t('settings.defaultGeofence'),
                t('settings.defaultGeofenceHelp'),
              )}
            </Stack>

            <Divider />

            <Alert severity="info">{t('settings.retentionNotice')}</Alert>
            {numberField(
              'locationRetentionDays',
              t('settings.retentionDays'),
              t('settings.retentionDaysHelp'),
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h2" gutterBottom>
            {t('settings.consentSection')}
          </Typography>
          <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
            {t('settings.consentNotice', { version: settings.data?.trackingConsentVersion ?? 1 })}
          </Alert>
          <Stack spacing={2}>
            <TextField
              label={`${t('settings.consentText')} (IT)`}
              value={form.consentTextIt}
              onChange={(event) => setForm({ ...form, consentTextIt: event.target.value })}
              multiline
              minRows={4}
              fullWidth
            />
            <TextField
              label={`${t('settings.consentText')} (TR)`}
              value={form.consentTextTr}
              onChange={(event) => setForm({ ...form, consentTextTr: event.target.value })}
              multiline
              minRows={4}
              fullWidth
            />
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h2" gutterBottom>
            {t('settings.salesSection')}
          </Typography>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label={t('settings.vatRates')}
              value={form.vatRates}
              onChange={(event) => setForm({ ...form, vatRates: event.target.value })}
              error={Boolean(fieldErrors.vatRates)}
              helperText={
                fieldErrors.vatRates ? t(fieldErrors.vatRates) : t('settings.vatRatesHelp')
              }
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.allowStaffPriceOverride}
                  onChange={(event) =>
                    setForm({ ...form, allowStaffPriceOverride: event.target.checked })
                  }
                />
              }
              label={t('settings.allowPriceOverride')}
            />
            <Typography variant="body2" color="text.secondary">
              {t('settings.allowPriceOverrideHelp')}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          size="large"
          onClick={submit}
          disabled={save.isPending}
          startIcon={save.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common.save')}
        </Button>
        <Button
          size="large"
          onClick={() => {
            if (settings.data) {
              setForm(toForm(settings.data));
              setFieldErrors({});
              formError.clear();
            }
          }}
          disabled={save.isPending}
        >
          {t('common.cancel')}
        </Button>
      </Stack>

      <Snackbar
        open={saved}
        autoHideDuration={3000}
        onClose={() => setSaved(false)}
        message={t('settings.saved')}
      />
    </Stack>
  );
}
